// frontend/src/components/common/ui/PageHeader.tsx
// Unified page header component for consistent page layouts

import React from 'react';
import { Search } from 'lucide-react';

// Utility function to combine class names
const cn = (...classes: (string | undefined | null | boolean)[]): string => {
  return classes.filter(Boolean).join(' ');
};

export interface PageHeaderAction {
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'ghost' | 'outline' | 'danger';
  disabled?: boolean;
  loading?: boolean;
}

export interface PageHeaderBadge {
  text: string;
  variant: 'info' | 'success' | 'warning' | 'error' | 'neutral';
}

export interface PageHeaderSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  onClear?: () => void;
}

export interface PageHeaderProps {
  // Content
  title: string;
  subtitle?: string;
  icon?: React.ComponentType<{ className?: string }>;
  
  // Actions
  primaryAction?: PageHeaderAction;
  secondaryActions?: PageHeaderAction[];
  
  // Status/Badges
  badge?: PageHeaderBadge;
  badges?: PageHeaderBadge[];
  
  // Context
  contextInfo?: React.ReactNode;
  breadcrumbs?: Array<{
    label: string;
    href?: string;
    onClick?: () => void;
  }>;
  
  // Search
  searchProps?: PageHeaderSearchProps;
  
  // Layout
  sticky?: boolean;
  variant?: 'default' | 'compact' | 'detailed' | 'minimal';
  className?: string;
  children?: React.ReactNode;
  hideActions?: boolean; // Hide actions from header (for external placement)
}

// Badge variant styles
const badgeVariants = {
  info: 'bg-blue-100 text-blue-800 border-blue-200',
  success: 'bg-green-100 text-green-800 border-green-200',
  warning: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  error: 'bg-red-100 text-red-800 border-red-200',
  neutral: 'bg-gray-100 text-gray-800 border-gray-200',
};

// Button variant styles
const buttonVariants = {
  primary: 'bg-blue-600 hover:bg-blue-700 text-white border-blue-600',
  secondary: 'bg-gray-600 hover:bg-gray-700 text-white border-gray-600',
  ghost: 'text-gray-700 hover:bg-gray-100 border-transparent',
  outline: 'text-gray-700 hover:bg-gray-50 border-gray-300 bg-white',
  danger: 'bg-red-600 hover:bg-red-700 text-white border-red-600',
};

// Size configurations
const sizeConfig = {
  default: {
    container: 'py-4',
    title: 'text-lg',
    subtitle: 'text-sm',
    icon: 'h-6 w-6',
    button: 'px-4 py-2 text-sm',
    badge: 'px-2.5 py-0.5 text-sm',
  },
  compact: {
    container: 'py-3',
    title: 'text-base',
    subtitle: 'text-sm',
    icon: 'h-5 w-5',
    button: 'px-3 py-1.5 text-sm',
    badge: 'px-2 py-0.5 text-xs',
  },
  detailed: {
    container: 'py-6',
    title: 'text-xl',
    subtitle: 'text-base',
    icon: 'h-8 w-8',
    button: 'px-5 py-2.5 text-base',
    badge: 'px-3 py-1 text-base',
  },
  minimal: {
    container: 'py-2',
    title: 'text-base',
    subtitle: 'text-xs',
    icon: 'h-4 w-4',
    button: 'px-3 py-1 text-xs',
    badge: 'px-2 py-0.5 text-xs',
  },
};

const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  icon: Icon,
  primaryAction,
  secondaryActions = [],
  badge,
  badges = [],
  contextInfo,
  breadcrumbs,
  searchProps,
  sticky = false,
  variant = 'default',
  className,
  children,
  hideActions = false,
}) => {
  const sizes = sizeConfig[variant];
  const allBadges = badge ? [badge, ...badges] : badges;
  
  const renderBreadcrumbs = () => {
    if (!breadcrumbs || breadcrumbs.length === 0) return null;
    
    return (
      <nav className="flex mb-2" aria-label="Breadcrumb">
        <ol className="flex items-center space-x-2">
          {breadcrumbs.map((crumb, index) => (
            <li key={index} className="flex items-center">
              {index > 0 && (
                <span className="text-gray-400 mx-2">/</span>
              )}
              {crumb.href || crumb.onClick ? (
                crumb.href ? (
                  <a
                    href={crumb.href}
                    className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    {crumb.label}
                  </a>
                ) : (
                  <button
                    onClick={crumb.onClick}
                    className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    {crumb.label}
                  </button>
                )
              ) : (
                <span className="text-sm text-gray-900">{crumb.label}</span>
              )}
            </li>
          ))}
        </ol>
      </nav>
    );
  };

  const renderAction = (action: PageHeaderAction, isPrimary = false) => {
    const ActionIcon = action.icon;
    
    return (
      <button
        key={action.label}
        onClick={action.onClick}
        disabled={action.disabled || action.loading}
        className={cn(
          'inline-flex items-center justify-center border font-medium rounded-md transition-colors duration-200',
          'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          'w-full sm:w-auto', // Full width on mobile, auto on desktop
          sizes.button,
          buttonVariants[action.variant || (isPrimary ? 'primary' : 'outline')]
        )}
      >
        {action.loading ? (
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
        ) : ActionIcon ? (
          <ActionIcon className="h-4 w-4 mr-2" />
        ) : null}
        {action.label}
      </button>
    );
  };

  const renderSearch = () => {
    if (!searchProps) return null;
    
    return (
      <div className="relative flex-1 max-w-md">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-4 w-4 text-gray-400" />
        </div>
        <input
          type="text"
          value={searchProps.value}
          onChange={(e) => searchProps.onChange(e.target.value)}
          placeholder={searchProps.placeholder || 'Search...'}
          className={cn(
            'block w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md',
            'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
            'placeholder-gray-400 text-gray-900'
          )}
        />
        {searchProps.onClear && searchProps.value && (
          <button
            onClick={searchProps.onClear}
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
          >
            <span className="text-gray-400 hover:text-gray-600">×</span>
          </button>
        )}
      </div>
    );
  };

  const headerClasses = cn(
    'bg-white border-b border-gray-200',
    sticky && 'sticky top-0 z-10',
    className
  );

  return (
    <div className={headerClasses}>
      <div className={cn('max-w-7xl mx-auto px-4 sm:px-6 lg:px-8', sizes.container)}>
        {/* Breadcrumbs */}
        {renderBreadcrumbs()}
        
        {/* Context Info */}
        {contextInfo && (
          <div className="mb-4">
            {contextInfo}
          </div>
        )}
        
        {/* Main Header Content */}
        <div className="space-y-4">
          {/* Title Row - Always on top */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* Left Side - Title and Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start">
                {/* Title */}
                <div className="min-w-0 flex-1">
                  <h1 className={cn(sizes.title, 'font-bold text-gray-900 truncate')}>
                    {title}
                  </h1>
                  {subtitle && (
                    <p className={cn(sizes.subtitle, 'text-gray-600 mt-1')}>
                      {subtitle}
                    </p>
                  )}
                </div>
                
                {/* Badges - Hide on mobile to save space */}
                {allBadges.length > 0 && (
                  <div className="hidden sm:flex flex-wrap gap-2 ml-4">
                    {allBadges.map((badgeItem, index) => (
                      <span
                        key={index}
                        className={cn(
                          'inline-flex items-center rounded-full border font-medium',
                          sizes.badge,
                          badgeVariants[badgeItem.variant]
                        )}
                      >
                        {badgeItem.text}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {/* Search - Only on tablet+ */}
            <div className="hidden sm:block">
              {renderSearch()}
            </div>
          </div>

          {/* Mobile Search - Below title on mobile */}
          <div className="sm:hidden">
            {renderSearch()}
          </div>

          {/* Badges Row - Below title on mobile */}
          {allBadges.length > 0 && (
            <div className="sm:hidden flex flex-wrap gap-2">
              {allBadges.map((badgeItem, index) => (
                <span
                  key={index}
                  className={cn(
                    'inline-flex items-center rounded-full border font-medium',
                    sizes.badge,
                    badgeVariants[badgeItem.variant]
                  )}
                >
                  {badgeItem.text}
                </span>
              ))}
            </div>
          )}
          
          {/* Actions Row - Only show if not hidden */}
          {!hideActions && (primaryAction || secondaryActions.length > 0) && (
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              {/* Primary Action - Full width on mobile */}
              {primaryAction && (
                <div className="sm:order-2">
                  {renderAction(primaryAction, true)}
                </div>
              )}
              
              {/* Secondary Actions - Below primary on mobile */}
              {secondaryActions.length > 0 && (
                <div className="flex flex-col sm:flex-row gap-2 sm:order-1">
                  {secondaryActions.map((action) => renderAction(action, false))}
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Additional Content */}
        {children && (
          <div className="mt-4">
            {children}
          </div>
        )}
      </div>
    </div>
  );
};

// Admin warning component for context info
export const AdminWarning: React.FC<{ message?: string }> = ({ 
  message = "You are viewing this as an administrator" 
}) => (
  <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
    <div className="flex">
      <div className="flex-shrink-0">
        <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
      </div>
      <div className="ml-3">
        <p className="text-sm text-amber-700">{message}</p>
      </div>
    </div>
  </div>
);

// Provider tip component for context info
export const ProviderTip: React.FC<{ message: string }> = ({ message }) => (
  <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
    <div className="flex">
      <div className="flex-shrink-0">
        <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
      </div>
      <div className="ml-3">
        <p className="text-sm text-blue-700">{message}</p>
      </div>
    </div>
  </div>
);

// Separate Actions Component for external placement
export const PageHeaderActions: React.FC<{
  primaryAction?: PageHeaderAction;
  secondaryActions?: PageHeaderAction[];
  variant?: 'default' | 'compact' | 'detailed' | 'minimal';
}> = ({ primaryAction, secondaryActions = [], variant = 'default' }) => {
  const sizes = sizeConfig[variant];
  
  const renderAction = (action: PageHeaderAction, isPrimary = false) => {
    const ActionIcon = action.icon;
    
    return (
      <button
        key={action.label}
        onClick={action.onClick}
        disabled={action.disabled || action.loading}
        className={cn(
          'inline-flex items-center justify-center border font-medium rounded-md transition-colors duration-200',
          'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          'w-full sm:w-auto', // Full width on mobile, auto on desktop
          sizes.button,
          buttonVariants[action.variant || (isPrimary ? 'primary' : 'outline')]
        )}
      >
        {action.loading ? (
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
        ) : ActionIcon ? (
          <ActionIcon className="h-4 w-4 mr-2" />
        ) : null}
        {action.label}
      </button>
    );
  };

  if (!primaryAction && secondaryActions.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-4 sm:mb-6">
      {/* Primary Action - Full width on mobile */}
      {primaryAction && (
        <div className="sm:order-2">
          {renderAction(primaryAction, true)}
        </div>
      )}
      
      {/* Secondary Actions - Below primary on mobile */}
      {secondaryActions.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-2 sm:order-1">
          {secondaryActions.map((action) => renderAction(action, false))}
        </div>
      )}
    </div>
  );
};

export default PageHeader;